import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../hooks/useAuth';
import Modal from '../components/shared/Modal';
import ConfirmModal from '../components/shared/ConfirmModal';
import Badge from '../components/shared/Badge';
import { format } from 'date-fns';

const EMPTY_USER = { name: '', email: '', password: '', role: 'designer', is_active: 1 };

export default function Users() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data),
  });

  const saveMut = useMutation({
    mutationFn: payload => {
      if (editUser) {
        const p = { ...payload };
        if (!p.password) delete p.password;
        return api.put(`/users/${editUser.id}`, p);
      }
      return api.post('/users', payload);
    },
    onSuccess: () => {
      toast.success(editUser ? 'User updated' : 'User created');
      qc.invalidateQueries(['users']);
      qc.invalidateQueries(['designers']);
      setModalOpen(false);
    },
  });

  const toggleActiveMut = useMutation({
    mutationFn: ({ id, is_active }) => api.put(`/users/${id}`, { is_active }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries(['users']); qc.invalidateQueries(['designers']); },
  });

  const deleteMut = useMutation({
    mutationFn: id => api.delete(`/users/${id}`),
    onSuccess: () => { toast.success('User deleted'); qc.invalidateQueries(['users']); qc.invalidateQueries(['designers']); },
  });

  const openCreate = () => { setEditUser(null); setForm(EMPTY_USER); setModalOpen(true); };
  const openEdit = u => { setEditUser(u); setForm({ name: u.name, email: u.email, password: '', role: u.role, is_active: u.is_active }); setModalOpen(true); };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => saveMut.mutate(form);

  return (
    <div>
      <div className="page-header">
        <h2>User Management</h2>
        <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>+ Add User</button>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : users.map(u => (
              <tr key={u.id}>
                <td><strong>{u.name}</strong></td>
                <td style={{ color: '#6c757d', fontSize: 12 }}>{u.email}</td>
                <td><Badge type="role" value={u.role} /></td>
                <td>
                  <div
                    className={`toggle ${u.is_active ? 'on' : ''}`}
                    onClick={() => { if (u.id !== me.id) toggleActiveMut.mutate({ id: u.id, is_active: u.is_active ? 0 : 1 }); }}
                    style={{ cursor: u.id === me.id ? 'not-allowed' : 'pointer' }}
                    role="presentation"
                  />
                </td>
                <td style={{ color: '#6c757d', fontSize: 12 }}>{u.created_at ? format(new Date(u.created_at), 'dd MMM yyyy') : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="ico-btn" onClick={() => openEdit(u)}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 2l3 3-9 9H2v-3l9-9z" /></svg>
                    </button>
                    {u.id !== me.id && (
                      <button type="button" className="ico-btn" onClick={() => setDeleteId(u.id)}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#dc3545" strokeWidth="1.5"><polyline points="2,4 14,4" /><path d="M5 4V2h6v2M6 7v5M10 7v5" /><rect x="3" y="4" width="10" height="10" rx="1" /></svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editUser ? 'Edit User' : 'Add User'}>
        <div className="form-group"><label>Full Name</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" /></div>
        <div className="form-group"><label>Email Address</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@deltamedia.in" /></div>
        <div className="form-group">
          <label>{editUser ? 'New Password (leave blank to keep)' : 'Password'}</label>
          <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={editUser ? 'Leave blank to keep current' : 'Set password'} />
        </div>
        <div className="form-group">
          <label>Role</label>
          <select value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="designer">Designer</option>
            <option value="manager">Manager</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#495057' }}>Active</label>
          <div className={`toggle ${form.is_active ? 'on' : ''}`} onClick={() => set('is_active', form.is_active ? 0 : 1)} role="presentation" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save User'}
          </button>
        </div>
      </Modal>

      <ConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)}
        title="Delete this user?" message="This action cannot be undone."
        confirmLabel="Delete" danger onConfirm={() => deleteMut.mutate(deleteId)} />
    </div>
  );
}
